import { InputAdornment, TextField, TextFieldProps } from "@mui/material";
import React from "react";
import { useFormContext, useController, Validate } from "react-hook-form";
import LabeledText from "./LabeledText";
import { isNullOrWhiteSpace, ResolvePath } from "helpers/objects";

interface IProps {
    fieldName: string,
    readOnly: boolean,
    label: string,
    requiredRule?: { value: boolean, message: string },
    validateValue?: Validate<any> | Record<string, Validate<any>>
    showPercentAdornment?: boolean,
    showRequiredAsterisk?: boolean
}

type TextFieldFormProps = TextFieldProps & IProps;

const TextFieldForm = ({ fieldName, readOnly, label, requiredRule, validateValue, onChange, onBlur, showPercentAdornment, showRequiredAsterisk, ...rest }: TextFieldFormProps) => {

    const { control, formState: { errors } } = useFormContext();
    const { field } = useController({
        name: fieldName,
        control: control,
        rules: {
            required: { value: requiredRule?.value ?? false, message: requiredRule?.message ?? "" },
            validate: validateValue
        },
    });

    function handleOnChange(e: any) {
        field.onChange(e);
        if (onChange) onChange(e);
    }

    function handleOnBlur(e: any) {
        field.onBlur();
        if (onBlur) onBlur(e);
    }

    const textFieldEndAdornment = React.useMemo(() => {
        if (showPercentAdornment) {
            return <InputAdornment position="end">%</InputAdornment>
        }
        return undefined;        
    }, [showPercentAdornment]);

    const textFieldLabel = React.useMemo(() => {
        if (isNullOrWhiteSpace(label)) {
            return "";
        }
        return `${showRequiredAsterisk ? "*" : ""}${label}`;
    }, [label, showRequiredAsterisk]);

    const textFieldText = React.useMemo(() => {
        if (field.value && showPercentAdornment) {
            return `${field.value} %`;
        }
        return field.value ?? "";        
    }, [showPercentAdornment, field.value]);    
    
    const getErrorMessage = React.useCallback(() => {
        const errorObj = ResolvePath(fieldName, errors);
        return errorObj?.message ?? "";
    }, [fieldName, errors]);

    if (readOnly) {
        return <LabeledText
            label={label}
            text={textFieldText}
            showSkeletonWhenEmpty={true}
        />
    }

    return <>
        <TextField
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            ref={field.ref}
            inputRef={field.ref}
            value={field.value ?? ""}
            error={!isNullOrWhiteSpace(getErrorMessage())}
            helperText={getErrorMessage()}
            label={textFieldLabel}
            InputProps={{ endAdornment: textFieldEndAdornment, ...rest.InputProps }}
            {...rest}
        />
    </>
}

export default TextFieldForm;